// web/src/api/quality/reports.ts —— 质检报告（列表 / 详情 / 导出 / 处置）
// 拆自 web/src/api/quality.ts 的同名分区（内容原样搬运）；由 web/src/api/quality.ts 以 export * 聚合，导出面对调用方不变。

import { del, get, post, put } from '../../request';
import { toPage, type Page, type PageQuery } from '../types';
import type { TranscriptSegment } from '../cdr';

import { parseDimensionScores } from './flow-appeal';

// ---------- 质检报告 ----------

/** 命中明细元素（后端返回解析后数组） */
export interface QualityHit {
  rule_name?: string;
  rule?: string; // 旧键兜底
  type?: string;
  deduct?: number;
  detail?: string;
  [k: string]: any;
}

/** 违规项（Score+Evidence；v1.10 点级化 Source/HumanNote） */
export interface QualityViolation {
  id: number;
  report_id: number;
  name: string;
  type: string;
  dimension?: string;
  level?: string;
  deduct: number;
  ai_reason?: string;
  /** 归属阶段（转人工双段质检）：ai=机器人段 / human=人工段；空=单段或未切分 */
  phase?: 'ai' | 'human' | '';
  evidence?: string; // JSON
  human_status: 'UNVERIFIED' | 'VERIFIED' | 'MISJUDGED' | 'CORRECTED';
  /** 人工改分后的**规则得分（0–5）**；展示/统计口径为百分制扣分，读取用 utils/quality 的 effectiveDeduct */
  corrected_score?: number | null;
  /** v1.10 点级化：ai=AI 检出 / human=人工补检 */
  source?: 'ai' | 'human';
  /** 人工复核分析 */
  human_note?: string;
  reviewed_by?: number | null;
  reviewed_at?: string | null;
}

/** 申诉点级明细（v1.10） */
export interface AppealItem {
  id: number;
  report_id: number;
  violation_id: number;
  agent_reason: string;
  verdict: 'pending' | 'upheld' | 'revoked' | 'rescore';
  reviewer_note?: string;
  /** 同 violation.corrected_score：0–5 得分；改分入参为正值扣分（0–100，20 的倍数） */
  corrected_score?: number | null;
  created_at?: string;
}

/** 人工补检入参（复核/申诉复核/抽检三处共用） */
export interface AddViolationIn {
  rule_id?: number;
  name?: string;
  type?: string;
  dimension?: string;
  level?: string;
  deduct: number;
  evidence?: string;
  note: string;
}

export interface QualityReport {
  id: number;
  cdr_id?: number;
  agent_id?: number;
  agent_name?: string;
  /** 坐席所属技能组名（空=未分组） */
  skill_group_name?: string;
  score?: number;
  status?: string;
  scene?: 'call' | 'chat' | 'flow';
  ref_chat_id?: number;
  hits?: QualityHit[] | string;
  transcript?: TranscriptSegment[];
  violations?: QualityViolation[];
  appeal_items?: AppealItem[];
  dimension_scores?: Record<string, number>;
  /** 双段独立得分（转人工双段质检）：nil/undefined=该段不存在（非 0 分）；score 恒为总分 */
  ai_score?: number | null;
  human_score?: number | null;
  /** 各段维度分（可能为空对象 {}）；防御性解析见 parseDimensionScores */
  ai_dimension_scores?: Record<string, number>;
  human_dimension_scores?: Record<string, number>;
  appeal_status?: string; // none|appealed|reviewed
  /** v2：触发来源 realtime（实时质检）/job（质检任务）/manual（人工单条）；空=存量旧口径 */
  source?: string;
  /** v2：本次评分所用质检模板 id（解释分数用） */
  template_id?: number | null;
  /** v2：报告归属的质检任务 id；NULL=实时链路或人工单条 */
  job_id?: number | null;
  /** v2：判定状态；undetermined=全模板规则均未判定，分数无意义且不计入平均分 */
  judge_status?: string;
  /** v2：判到低分（≤2）的规则名，逗号分隔（服务维度列表直接展示「哪几条没做好」） */
  low_rule_names?: string;
  /** v2：逐规则得分明细（报告详情才有） */
  rule_scores?: Array<{
    id: number;
    report_id: number;
    rule_id: number;
    rule_set_id?: number;
    rule_name: string;
    level?: string;
    awarded: number;
    weight_in_set?: number;
    eff_weight?: number;
    weighted?: number;
    reason?: string;
    judge_status?: string;
  }>;
  appeal_reason?: string;
  appeal_reply?: string;
  /** 单通报告 AI 分析（JSON 字符串：{conclusion,strengths,weaknesses,actions}；解析见 parseReportAIAnalysis） */
  ai_analysis?: string;
  /** 单通报告 AI 分析状态：''|pending|running|done|failed */
  ai_analysis_status?: string;
  created_at?: string;
  [k: string]: any;
}

/** 单通报告 AI 分析（quality_report.ai_analysis 解析结果） */
export interface ReportAIAnalysis {
  conclusion?: string;
  strengths?: string[];
  weaknesses?: string[];
  actions?: string[];
  /** AI 多维诊断（68 维；清单不可用时可能为空） */
  dimensions?: DimensionResult[];
}

/** 单通 AI 多维诊断的单个维度结果 */
export interface DimensionResult {
  code: string;
  name: string;
  category: string;
  /** good|warn|bad|na（na=数据不足不评判） */
  verdict: string;
  evidence?: string;
  advice?: string;
  score: number;
}

/** parseReportAIAnalysis 容错解析单通报告 AI 分析 JSON（空/坏数据返回 null） */
export function parseReportAIAnalysis(raw?: string): ReportAIAnalysis | null {
  if (!raw) return null;
  try {
    const v = JSON.parse(raw);
    return v && typeof v === 'object' ? (v as ReportAIAnalysis) : null;
  } catch {
    return null;
  }
}

/** GET /quality/reports?scene=call|chat&appeal_status=&agent_id=&job_id=&cdrId=&ref_chat_id=&object_no=（后两者为单条质检面板按记录取报告用；object_no 为「编号」搜索） */
export async function listReports(
  q: PageQuery & {
    appeal_status?: string;
    scene?: string;
    agent_id?: number;
    job_id?: number;
    /** 通话记录 id（后端 query 名为 cdrId） */
    cdrId?: number;
    /** 在线会话 id */
    ref_chat_id?: number;
    /** 按「编号」搜索：报告编号 / 通话 CDR / 在线会话号任一相等即命中 */
    object_no?: number;
    /** 触发来源：realtime（实时质检，服务维度列表用）/job（质检任务）/manual（人工单条） */
    source?: string;
    /** '1'=只看有低分规则（awarded ≤2）的服务 */
    low_only?: string;
    /** 'score_asc'=按总分升序（把问题服务顶上来） */
    sort?: string;
  }
): Promise<Page<QualityReport>> {
  return toPage<QualityReport>(await get('/quality/reports', q));
}

/** 单条质检可选过滤：规则集 id / 规则 id（rule_ids 优先），空=全量启用规则 */
export interface RunQualityOptions {
  rule_set_ids?: number[];
  rule_ids?: number[];
  /** true=已有报告时强制重跑（先删旧报告与复核样本） */
  force?: boolean;
}

/** POST /quality/cdrs/:id/run（管理端对单条通话发起质检；已有报告且未 force 返回 409） */
export function runCdrQuality(cdrId: number, opts?: RunQualityOptions) {
  return post(`/quality/cdrs/${cdrId}/run`, opts || {});
}

/** POST /quality/chat-sessions/:id/run（管理端对单条在线会话发起质检；已有报告且未 force 返回 409） */
export function runChatQuality(sessionId: number, opts?: RunQualityOptions) {
  return post(`/quality/chat-sessions/${sessionId}/run`, opts || {});
}

